import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { XCircle } from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface CancelSubscriptionDialogProps {
  onConfirm: () => void;
  renewsAt: string | Date;
  disabled?: boolean;
}

export function CancelSubscriptionDialog({
  onConfirm,
  renewsAt,
  disabled = false,
}: CancelSubscriptionDialogProps) {
  const [open, setOpen] = useState(false);

  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  const endDate = formatDate(renewsAt);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          className="flex-1"
          disabled={disabled}
        >
          <XCircle className="h-4 w-4 mr-2" />
          {disabled ? "Subscription Ending" : "Cancel Subscription"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period on{' '}
                <strong className="text-foreground">{endDate}</strong>.
              </p>
              <p className="text-sm">
                You can reactivate your subscription anytime before it expires, and you won't be charged again unless you reactivate or subscribe to a new plan.
              </p>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-500">
                After {endDate}, you'll lose access to premium features and your account will be downgraded to the free tier.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            Keep Subscription
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-destructive hover:bg-destructive/90"
          >
            Cancel Subscription
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
